import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Typography from '@material-ui/core/Typography';
import MuiChip from '@material-ui/core/Chip';
import AccessAlarmsIcon from '@material-ui/icons/AccessAlarms';
import PropTypes from 'prop-types';
import styled from '@material-ui/styles/styled';

const Chip = styled(MuiChip)(({ theme }) => ({
	background: theme.palette.error.main,
}));

function CountdownTimer({
	timeout,
	alertThreshold,
	onStart,
	onTimeout,
	onTimerAlert,
	...props
}) {
	const [secondsRemaining, setSecondsRemaining] = useState(parseInt(timeout));

	const intervalRef = useRef();

	const decrement = async () => {
		setSecondsRemaining((sR) => sR - 1);
	};

	const cancel = useCallback(() => {
		clearInterval(intervalRef.current);
		intervalRef.current = undefined;
		setSecondsRemaining(timeout);
	}, [intervalRef, setSecondsRemaining, timeout]);

	if (secondsRemaining <= 0) {
		cancel();
		onTimeout();
	}

	const startInterval = useCallback(() => {
		intervalRef.current = setInterval(decrement, 1000);
	}, [intervalRef]);

	const reset = useCallback(() => {
		cancel();
		startInterval();
	}, [cancel, startInterval]);

	useEffect(() => {
		startInterval();
		onStart({
			cancel,
			reset,
		});
		return cancel;
	}, [reset, cancel, startInterval, onStart]);

	const isChipShown = useMemo(
		() => secondsRemaining <= alertThreshold,
		[secondsRemaining, alertThreshold]
	);

	useEffect(() => {
		if (isChipShown) {
			onTimerAlert();
		}
	}, [onTimerAlert, isChipShown]);

	let displaySecondsRemaining = Math.ceil(secondsRemaining % 60);
	let minutesRemaining = Math.floor(secondsRemaining / 60) || '';
	if (secondsRemaining === 60) {
		minutesRemaining += 1;
		displaySecondsRemaining = 0;
	}
	if (displaySecondsRemaining < 10) {
		displaySecondsRemaining = `0${displaySecondsRemaining}`;
	}

	return (
		isChipShown && (
			<Chip
				size="medium"
				icon={<AccessAlarmsIcon />}
				label={
					<Typography variant="body2">{`${minutesRemaining}:${displaySecondsRemaining}`}</Typography>
				}
				clickable
				id="timer-alert"
				color="primary"
				{...props}
			/>
		)
	);
}

CountdownTimer.propTypes = {
	timeoutMs: PropTypes.number,
	alertThresholdMs: PropTypes.number,
	onStart: PropTypes.func,
	onTimeout: PropTypes.func,
	onTimerAlert: PropTypes.func,
};

CountdownTimer.defaultProps = {
	timeout: 30,
	onStart: () => {},
	onTimeout: () => {},
	onTimerAlert: () => {},
	alertThreshold: 10,
};

export default React.memo(CountdownTimer);
